import ValidState from "common/enums/validators/ValidState";
import LoadingBar from "common/widgets/LoadingBar/LoadingBar";
import DFSwitch from "common/widgets/DFSwitch/DFSwitch";
import GrowType from "common/enums/form/responsive/GrowType";
import PromptInputType from "common/enums/PromptInputType";

var widget;
widget = kendo.ui.Widget.extend({

    options: {
        name: "PromptInput",
        model: kendo.observable({}),
        template: "<input>",
        errorTemplate: '<div class="validationWrap" aria-label="#if(data && data.join){##:data.join(\',\')##}#"><span class="k-widget k-tooltip k-tooltip-validation">' +
            '<span aria-hidden="true" aria-label="Warning"><span class="k-icon k-warning"> </span></span>' +
            ' # for(var i = 0; i < data.length; ++i) { # ' +
            '<span class="text">#:data[i]#</span>' +
            ' # } # ' +
            '</span></div>',
        parentId: "", //The id of the parent prompt list
        labelledBy: [],
        id: "", //The id that should be used for the input
        validateOnBlur: false, // whether to listen for blur events and call validateInput
        enterMovesFocus: false, // whether the enter key acts like a tab (indexing)

        // applies the disabled state based on the value of readonly (when readonly is called)
        disabledWhenReadonly: false,
        // info to show when input is empty
        emptyInfo: false
    },

    events: [
        // triggered when user requests focus to the next input
        "focusNext",
        // triggered when user requests focus to the previous input
        "focusPrev",
        // triggered when an input is focused
        "inputFocus",
        // triggered when initial value is set on the PromptInput using logic specific to the rendered widget
        "valueParse",
        // when size is set to a corrected calculation
        "sizeCorrected"
    ],

    /**
     * Initialize the common widget options.  NOTE: implementing classes should call this.refresh() if you
     * need to extend init.  We do not implement it here in the base class because you may want to defer
     * calling it until after the widget is configured.
     *
     * @param element
     * @param options
     */
    init: function(element, options) {
        kendo.ui.Widget.fn.init.apply(this, arguments);

        this._refresh = $.proxy(this._refresh, this);
        this.handleChange = $.proxy(this.handleChange, this);
        this._focused = $.proxy(this._focused, this);

        this.input = $(this.options.template);
        if(this.options.model.required) {
            this.input.attr("required", true);
        }
        this.input.attr("id", this.options.parentId + "_" + this.options.id);
        this.input.attr("name", this.input.attr("id"));
        this.input.attr("data-field", this.options.model.name);
        this._validationId = kendo.guid();
        this.element.append(this.input);

        var preloaderEl = $("<div/>", {
            id: this.input.attr("id") + "_preloader",
            tabindex: "0"
        });
        this.element.append(preloaderEl);
        this.preloader = new LoadingBar(preloaderEl, {});

        this.options.model.bind("change", this._refresh);

        this.options.x = this.options.model.x;
        this.options.y = this.options.model.y;
        this.options.width = this.options.model.width;
        this.options.height = this.options.model.height;

        this._updateHidden();
        this._updateRequired();
        this._updateDisabled();
        this._updateExtraClasses();
    },

    _blurrable: function() {
        return this.input;
    },

    onInit: function(promptList) {
        this._initOverrideError();
        this.tabbable(this.options.tabbable);
        this._focusable();

        // validateOnBlur is used for CustomValidators, in order to run client side validation on blur.
        // It will not perform server validation on focus out, only when a value is changed.
        // Some widgets have not yet created the input that needs to listen to blur on init, so this needs to be in onInit.
        if(this.options.validateOnBlur) {
            this._blurrable().on("blur", () => {
                this.trigger("blur");
            });
            this.customValidateInput = promptList.validateInput.bind(promptList);
        }
        this.setARIALabel();
    },

    /**
     * Detect when user is attempting to move focus if focus is supported
     * @private
     */
    tabbable: function(to) {
        this.options.tabbable = to !== false;
        if(this.options.tabbable === false) {
            this.disableTabbable();
        } else if(this.wrapper) {
            var me = this;
            this.wrapper.off("keydown.promptInput").on("keydown.promptInput", function(e) {
                if(me._isTab(e)) {
                    me._tabbed(e);
                }
            });
        }
    },

    disableTabbable: function() {
        this.input.attr("tabindex", "-1");
    },

    _isTab: function(e) {
        return e.which === kendo.keys.TAB || this.options.enterMovesFocus === true && e.which === kendo.keys.ENTER;
    },

    /**
     * Event handler for when a user requests keyboard focus movement
     * @param e
     * @private
     */
    _tabbed: function(e) {
        var prevented = e.shiftKey === true ? this._focusPrev() : this._focusNext();
        if(prevented) {
            // a prompt sibling was found and focused, trap the event
            e.preventDefault();
            e.stopPropagation();
        }
    },

    /**
     * Add focus handlers to tabbable fields so focus state can be tracked by prompt list
     * @private
     */
    _focusable: function() {
        this.getAllFocusables().forEach(function(focusable) {
            focusable.unbind("focused", this._focused);
            focusable.bind("focused", this._focused);
        }, this);
    },

    /**
     * Adds widgets to list of focusable items for this prompt
     * Only needs to be called for prompts that have dynamic inputs like ones that use ManyInput
     * @param widgets {Array} List of widgets to make focusable
     */
    addFocusables: function(widgets) {
        widgets.forEach(function(focusable) {
            focusable.unbind("focused", this._focused);
            focusable.bind("focused", this._focused);
        }, this);
    },

    /**
     * Handler for when a widget is focused
     * @private
     */
    _focused: function(e) {
        this._focusedWidget = e.sender;
        this.trigger("inputFocus");
    },

    /**
     * Gets all widgets that can be focused regardless of current visibility
     * @returns {Array.<*>}
     */
    getAllFocusables: function() {
        return []
            .concat(this._getFocusPreloader())
            .concat(this._getFocusWidgets(true))
            .concat(this._getFocusOverride());
    },

    /**
     * Find the target(s) for keyboard events.
     * @return {jQuery} The collection of inputs that can receive focus
     */
    getFocusables: function() {
        return []
            .concat(this.isLoading() ? this._getFocusPreloader() : this._getFocusWidgets())
            .concat(this.isOverrideErrorVisible() ? this._getFocusOverride() : []);
    },

    /**
     * The preloader
     * @returns {Array} The preloader widget
     * @private
     */
    _getFocusPreloader: function() {
        return this.preloader ? [this.preloader] : [];
    },

    /**
     * The standard collection of elements that can receive focus
     * @param showAll {boolean} Whether to return all widgets or just the visible ones
     * @returns {Array} The widgets that can be focused
     * @private
     */
    _getFocusWidgets: function(showAll) {
        return this.widget ? [this.widget] : [];
    },

    /**
     * Gets the override checkbox if the prompt supports override and the value is in error state
     * @private
     */
    _getFocusOverride: function() {
        return this._overrideCheckbox ? [this._overrideCheckbox] : [];
    },

    /**
     * Gets the current focused element
     * @returns {jQuery} The element in the widget that has focus (will be one of the elements returned from getFocusables
     * @private
     */
    _getFocused: function() {
        return this._focusedWidget;
    },

    /**
     * Trigger focus event
     * @param eventName {string} - focusPrev or focusNext
     * @returns {boolean} Whether an input was focused or not
     * @private
     */
    _triggerFocusNav(eventName) {
        // manually trigger change so dependency executor runs before next focus prompt is found
        this.commitValue();
        return this.trigger(eventName);
    },

    /**
     * Trigger widget value commit so widget value is reported correctly before a complete browser input blur occurs
     * This is needed because of the custom focus handling in PromptList
     * TAB detection has to act at a specific time that is sometimes before some widgets' value() reports the value
     */
    commitValue() {
        this.handleChange();
    },

    /**
     * Focus the previous input in this prompt (supports multiple inputs for widgets like ManyInput and BlindInput)
     * @returns {boolean} Whether an input was focused or not
     * @private
     */
    _focusPrev: function() {
        var focusables = this.getFocusables();
        if(focusables.length <= 1) {
            return this._triggerFocusNav("focusPrev");
        } else {
            // find prev element if there is one
            var prev;
            for(var i = 0; i < focusables.length; i++) {
                if(this._getFocused().element[0] !== focusables[i].element[0]) {
                    prev = focusables[i];
                    continue;
                }
                break;
            }
            if(prev) {
                this._focus(prev);
                return true;
            } else {
                return this._triggerFocusNav("focusPrev");
            }
        }
    },

    /**
     * Focus the next input in this prompt (supports multiple inputs for widgets like ManyInput and BlindInput)
     * @returns {boolean} Whether an input was focused or not
     * @private
     */
    _focusNext: function() {
        var focusables = this.getFocusables();
        if(focusables.length <= 1) {
            return this._triggerFocusNav("focusNext");
        } else {
            // find next element if there is one
            var next;
            for(var i = focusables.length - 1; i >= 0; i--) {
                if(this._getFocused().element[0] !== focusables[i].element[0]) {
                    next = focusables[i];
                    continue;
                }
                break;
            }
            if(next) {
                this._focus(next);
                return true;
            } else {
                return this._triggerFocusNav("focusNext");
            }
        }
    },

    _refresh: function(e) {
        if(e.field == "value" || e.field == "errorsOverridden" || e.field == "blindValue") {
            this.refresh();
        } else if(e.field == "validMessage") {
            this._updateValidation();
        } else if(e.field == "validState") {
            this._handleOverrideErrors();
        } else if(e.field == "loading") {
            this._updatePreloader();
        } else if(e.field == "hidden") {
            this._updateHidden();
        } else if(e.field == "required") {
            this._updateRequired();
        } else if(e.field == "disabled") {
            this._updateDisabled();
        } else if(e.field === "extraClasses") {
            this._updateExtraClasses();
        }
    },

    _extraClasses: "",

    _updateExtraClasses: function() {
        if(this.wrapper) {
            this.wrapper.removeClass(this._extraClasses);
            this.wrapper.addClass(this.options.model.get("extraClasses"));
        }
        this._extraClasses = this.options.model.get("extraClasses");
    },

    _updateValidation: function() {
        var messages = this.options.model.get("validMessage"),
            state = this.options.model.get("validState"),
            errorIndices = this.options.model.get("errorIndices") || [];

        if(!state) {
            this._updateInfo();
            return;
        }
        var valid = state === ValidState.VALID,
            warning = state === ValidState.WARNING,
            error = state === ValidState.ERROR,
            name = this.input.attr("name");
        if(valid) {
            this._updateInfo();
        } else if(messages && messages.length > 0) {

            // detect whether this is a multiple-value string field, and an error index was specified.
            // if so, only show the tooltip on that field.
            var multiValue = this.options.model.get("allowMultipleValues") && errorIndices.length > 0;
            if(multiValue) {
                var nameSelectors = [];
                errorIndices.forEach($.proxy(function(errorIndex) {
                    const wrapperName = `${name}_option${errorIndex}`;
                    const inputName = `${wrapperName}_input`;
                    nameSelectors.push("[data-for='" + inputName + "']");
                    this._setErrorTooltipMulti(wrapperName, inputName, messages, warning ? "warning" : "");
                }, this));

                // if we are in multiple-value strings, there could be old tooltips around that we need to hide
                this.element.find(".k-invalid-msg").not(nameSelectors.join(",")).hide();
            } else {
                this._setErrorTooltipSingle(name, messages, warning ? "warning" : "");
            }
        }
        this.input.toggleClass("k-invalid", error);
        this.input.toggleClass("k-warning", warning);
        this.input.toggleClass("k-valid", valid);
    },

    setEmptyInfo(message) {
        this.options.emptyInfo = message;
    },

    _updateInfo() {
        const message = this.options.emptyInfo;
        const value = this.options.model.get("value");
        const required = this.options.model.get("required");
        const metadata = this.options.model.get("entityType") === "METADATA";
        if(message && metadata && !required && (value === null || value === undefined || value.length === 0)) {
            this._setErrorTooltipSingle(this.input.attr("name"), [message], "information");
        } else {
            this._removeErrorTooltip();
        }
    },

    _removeErrorTooltip() {
        this.element.find(".k-invalid-msg").remove();
        this.setDescribedBy("");
    },

    _setErrorTooltipSingle(inputName, messages, className) {
        this._setErrorTooltip(...arguments, (el) => {
            this.element.append(el);
        });
    },

    _setErrorTooltipMulti(wrapperName, inputName, messages, className) {
        this._setErrorTooltip(inputName, messages, className, (el) => {
            this.element.find(`#${wrapperName}>:first-child`).after(el);
        });
    },

    _setErrorTooltip: function(name, messages, className, appenderFn) {
        // create a label to use for a tooltip

        var label = $($.parseHTML(kendo.template(this.options.errorTemplate)(messages)))
            .addClass("k-invalid-msg")
            .attr("id", this._validationId)
            .attr("data-for", name);

        // style warnings so they look different
        if(className) {
            label.addClass(className);
            label.attr("role", "alert");
        }

        // replace existing warnings if found, otherwise, insert a new one.
        let existing = this.element.find(".k-invalid-msg[data-for='" + name + "']");
        if(!existing.replaceWith(label).length) {
            appenderFn(label);
        }
        label.show();

        //This code is running during blur. If, during blur, you add an aria-describedby Chrome/NVDA will read the new description.
        //Since the describedby also has a role=alert, that causes it to read multiple times.
        //By adding setTimeout, the description is not set during blur and the extra announcements go way.
        //The other option(remove the role=alert), causes Firefox/NVDA to never read the description.
        setTimeout(() => {
            this.setDescribedBy(this._validationId);
        }, 0);
    },

    _overrideCheckbox: undefined,

    /**
     * Initialize the override error checkbox
     * @private
     */
    _initOverrideError: function() {
        if(this.options.model.get("allowOverrideErrors") === true) {
            var me = this;
            var el = $("<div/>", {
                id: this.input.attr("id") + "_override",
                "class": "overrideErrors"
            });
            this.widget.wrapper.parent().after(el);
            this._overrideCheckbox = new DFSwitch(el, {
                label: "doc.override",
                change: function() {
                    me.options.model.set("errorsOverridden", this.model.get("value"));
                }
            });
            this._overrideCheckbox.widget.add(this._overrideCheckbox.widget.find("input")).attr("data-for", this.input.attr("id"));
            this._overrideCheckbox.wrapper.hide();
        }
    },

    /**
     * Get the current state of the override error checkbox
     * @returns {boolean} Whether the override error checkbox is visible or not
     */
    isOverrideErrorVisible: function() {
        return this.options.model.get("allowOverrideErrors") === true && this.options.model.get("validState") === ValidState.ERROR && this._overrideCheckbox;
    },

    _handleOverrideErrors: function() {
        if(this.isOverrideErrorVisible()) {
            this._overrideCheckbox.wrapper.show();
            this._overrideCheckbox.value(this.options.model.get("errorsOverridden") === true);
        } else if(this._overrideCheckbox) {
            this._overrideCheckbox.wrapper.hide();
            this._overrideCheckbox.value(false);
        }
    },

    /**
     * Convert value after getting from input.
     * @param x
     * @returns {*}
     */
    parseValue: function(x) {
        return x;
    },

    /**
     * Convert value before sending to input.
     * @param x
     * @returns {*}
     */
    formatValue: function(x) {
        return x;
    },

    /**
     * This needs to be extended by each implementation
     */
    destroy: function() {
        this.getAllFocusables().forEach((focusable) => {
            focusable.unbind("focused", this._focused);
        });
        if(this._overrideCheckbox) {
            var overrideEl = this._overrideCheckbox.element;
            this._overrideCheckbox.destroy();
            overrideEl.remove();
            delete this._overrideCheckbox;
        }

        if(this.widget) {
            this.widget.destroy();
            delete this.widget;
        }

        if(this.input) {
            this.input.off();
            this.input.remove();
            delete this.input;
        }
        if(this.preloader) {
            var preloaderEl = this.preloader.element;
            this.preloader.destroy();
            preloaderEl.remove();
            delete this.preloader;
        }

        this.options.model.unbind("change", this._refresh);

    },

    /**
     * This needs hooked up by each implementation.  It should fire when the UI component changes. It does not need changed by each implementation
     */
    handleChange: function() {
        this.options.model.set("value", this.value());
    },

    /**
     * This will be called whenever the attached model's value field is changed.
     * It should also be called after init.
     *
     * NOTE: if you override this method, make sure to call out to the parent implementation using
     * PromptInput.fn.refresh.apply(this, arguments).
     * If you override the method and fail to call back into the parent, you will get odd behavior.
     *
     * Generally you should not need to override refresh, consider overriding updateWidgetValue instead.
     *
     */
    refresh: function() {
        //This needs to sync the UI component to the model
        var value = this.options.model.get("value");
        this.updateWidgetValue(this.formatValue(value));
        this.trigger("inputChange");

        // update validation.
        this._updateValidation();

        // update override errors state from the model
        this._handleOverrideErrors();

        this._updatePreloader();
    },

    /**
     * Updates the widget with the current value.  Extending classes may overwrite this method freely, without adversely affecting
     * other behavior that happens on refresh.  NOTE the value has already been formatted.
     *
     * @param value the new value, already formatted
     */
    updateWidgetValue: function(value) {
        this.widget.value(value);
    },

    /**
     * This function must return the value of the UI component
     */
    value: function() {
        return this.parseValue(this.widget.value());
    },

    getData: function() {
        var data = this.options.model.data;
        if(data && data.length) {
            return data.filter(function(item) {
                return item.value !== undefined && item.value !== null && item.value !== "";
            });
        } else {
            return [];
        }
    },

    /**
     * Gets the display text for the control
     * @returns {*}
     */
    text: function() {
        return this.value();
    },

    /**
     * Enable readonly support for the widget.  Default implementation assumes that the input widget has a
     * readonly method that can be called.  If that is not true, subclasses can override this method to add
     * custom behavior.
     *
     * @param val readonly value
     */
    readonly: function(val) {
        this._readonly(val);
        this._readonlyEnable(val);
    },

    _readonly: function(val) {
        val = this._isReadonly(val);
        if(this.widget && this.widget.readonly) {
            this.widget.readonly(val);

            this.element.toggleClass("readOnlyPrompt", val);

            if(this.widget.wrapper) {
                if(val) {
                    this.widget.wrapper.addClass("readonly");
                } else {
                    this.widget.wrapper.removeClass("readonly");
                }
            }
        }
        if(this.element) {
            this.element.find("label[data-requiredField='true']").toggleClass("requiredField", !val);
        }
    },


    _readonlyEnable: function(isReadonly) {
        if(this.options.disabledWhenReadonly) {
            this.enable(!this._isReadonly(isReadonly));
        }
    },

    _isReadonly: function(val) {
        return val || (this.options.model.readonly !== undefined ? this.options.model.readonly : false);
    },

    /**
     * Enable support for the widget.  Default implementation assumes that the input widget has an
     * enable method that can be called.  If that is not true, subclasses can override this method to add
     * custom behavior.
     *
     * @param val enable value
     */
    enable: function(val) {
        if(this.widget && this.widget.enable) {
            this.widget.enable(val && !this.options.model.disabled);
        }
    },

    /**
     * Update required flag.
     * @param val
     */
    setRequired: function(val) {
        var label = this._getRequiredLabel();
        if(val) {
            this.input.attr("required", true);
            label.attr("data-requiredField", true);
        } else {
            const isRequired = this.input.is("[required]");
            this.input.removeAttr("required");
            label.removeAttr("data-requiredField");
            isRequired && this.customValidateInput && this.customValidateInput(this.options.model, true);
        }
        label.toggleClass("requiredField", !!val);
        label.toggleClass("conditionalRequired", !val && !!this.options.model.requiredMacro);
    },

    /**
     * Update required conditional class
     * @param val
     */
    setRequiredConditional: function(val) {
        this._getRequiredLabel().toggleClass("conditionalRequired", !!val);
    },

    _getRequiredLabel: function() {
        return this.element.closest(".singlePromptContainer").find("label[data-field]");
    },

    /**
     * Determine if this prompt can be focused right now.
     * @returns {boolean} True if prompt can be focused
     */
    canFocus: function() {
        return !!this.options.model && !this.options.model.get("hidden") && !this.options.model.get("disabled") && !this.options.model.get("readonly");
    },

    /**
     * Special focus condition called when the PromptList initially loads and sets initial focus
     * can be overridden to apply special first-focus rules for specific prompt types
     * @returns {boolean}
     */
    canFocusInitially: function() {
        return this.canFocus();
    },

    /**
     * Determine if prompt is controlled by a datasource and the datasource returned data
     * Used to skip focus of prompts that don't have data but are populated via datasource
     * @returns {boolean} Whether the datasource returned data or not
     */
    canFocusDatasource: function() {
        const haveData = this.hasData();
        const loaded = !this.isLoading();
        const noDatasource = !this.options.model.get("datasourceId")
            && this.options.model.get("inputType") !== PromptInputType.LIST;
        const isEditable = this.canControlAcceptFocusWithNoData();
        return noDatasource || (loaded && isEditable) || (loaded && haveData);
    },

    hasData() {
        return this.getData()?.length > 0;
    },

    /**
     * Is the currently rendered control of a type that could accept focus in the right circumstances.
     * @returns {boolean}
     */
    canControlAcceptFocusWithNoData: function() {
        return true;
    },

    /**
     * Whether the prompt input can receive focus when the prompt list is readonly
     * @returns {boolean}
     */
    canFocusWhenPromptListReadonly: function() {
        return false;
    },

    /**
     * Get the loading status of the prompt (controlled via DependencyExecutor)
     * @returns {boolean} Whether the prompt is loading or not
     */
    isLoading: function() {
        return this.options.model.get("loading") === true;
    },

    /**
     * Focus the prompt input
     * @param focusLastItem {boolean} if the focus is moving in reverse (i.e. shift key is used) then focus the last item
     */
    focus: function(focusLastItem) {
        var targets = this.getFocusables();
        if(targets && targets.length > 0) {
            var target = targets[!focusLastItem ? 0 : targets.length - 1];
            this._focus(target);
        }
    },

    restoreFocus() {
        if(this._focusedWidget && this.getFocusables().includes(this._focusedWidget)) {
            this._focus(this._focusedWidget);
        } else {
            this.focus();
        }
    },

    _focus: function(focusable) {
        focusable.focus();
    },

    setDescribedBy: function(id) {
        if(this.widget?.setDescribedBy) {
            this.widget?.setDescribedBy("PROMPT_LIST", id);
        } else {
            this.input?.attr("aria-describedby", id);
        }
    },

    setARIALabel: function() {
        const labelledBy = this.options.labelledBy.join(" ");
        if(this.widget && this.widget.setLabelledBy) {
            this.widget.setLabelledBy("PROMPT_LIST", labelledBy);
        } else {
            this.input.attr("aria-labelledby", labelledBy);
        }
    },

    _updateHidden: function() {
        if(this.options.model.get("hidden")) {
            this.element.closest(".singlePromptContainer").addClass("hideImportant");
        } else {
            this.element.closest(".singlePromptContainer").removeClass("hideImportant");
        }
    },

    _updateDisabled: function() {
        this.enable(!this.options.model.get("disabled"));
    },

    _updateRequired: function() {
        this.setRequired(!!this.options.model.get("required"));
    },

    _updatePreloader: function() {
        if(this.isLoading()) {
            this._loadingDisplayed = true;
            this._toggleInput(false);
            this._togglePreloader(true);
        } else {
            if(this._loadingDisplayed) {
                this._toggleInput(true);
            }
            this._loadingDisplayed = false;
            this._togglePreloader(false);
        }
    },

    _togglePreloader: function(visible) {
        if(this.preloader) {
            if(visible) {
                this.preloader.value(true);
                this.preloader.element.show();
            } else {
                this.preloader.value(false);
                this.preloader.element.hide();
            }
        }
    },

    _toggleInput: function(visible) {
        if(this.input) {
            var el = this.widget ? (this.widget.wrapper ? this.widget.wrapper : this.widget.element) : this.input;
            el.toggleClass("loadingInput", !visible);
        }
    },

    /**
     * Determine whether to toggle a placeholder class on the element.  It should get a placeholder when the
     * control has a datasource, is read-only, and has no value.
     * The placeholder will be styled to look like an empty, disabled input.
     */
    toggleDatasourcePlaceholder: function() {
        this.element.toggleClass("fakeInput", this.options.model.datasourceId !== undefined && this.options.model.readonly && (!this.options.model.data || this.options.model.data.length === 0));
    },

    /**
     * Styles the prompt container with an invalid class if invalid, removes it otherwise
     * @param isInvalid
     */
    markInvalid: function(isInvalid) {
        this.wrapper && this.wrapper.toggleClass("invalidConfig", isInvalid);
    },

    /**
     * Updates visual representation of a prompt like position and size
     * Will likely be extended to set specific input visuals
     */
    updateVisual: function(skipSizeReset) {
        this.updatePosition();
        this.updateStyle();
        this.updateSize(skipSizeReset);
    },

    /**
     * Sets the position of the prompt input
     */
    updatePosition: function() {
        // reset to default
        this.element.parent().css({
            left: "",
            top: ""
        });
        if(this.options.x !== undefined && this.options.x !== null && this.options.x >= 0 && this.options.y !== undefined && this.options.y !== null && this.options.y >= 0) {
            this.element.parent().css({
                left: this.options.x + "px",
                top: this.options.y + "px"
            });
        }
    },

    hasTheme: function(hasTheme) {
        if(arguments.length === 0) {
            return this.options.hasTheme;
        }
        this.options.hasTheme = hasTheme;
        this.updateStyle();
        this.updateSize();
    },

    /**
     * Update non-sizing style on the prompt input
     */
    updateStyle: function() {
        // most styles are set by extensions of prompt input
        this.wrapper.toggleClass("noLabel", !this.options.model.label);
        if(this.label) {
            this._setFontStyle(this.label);
        }
        this._setFontSizeAndFamily();
    },

    /**
     * Sets font style on an element, not related to the size or family
     * @param element
     * @private
     */
    _setFontStyle: function(element) {
        element.toggleClass("bold", !this.hasTheme() && this.options.model.isBold === true);
        element.toggleClass("unbold", !this.hasTheme() && this.options.model.isBold === false);
        element.toggleClass("italic", !this.hasTheme() && this.options.model.isItalic === true);
        element.toggleClass("unitalic", !this.hasTheme() && this.options.model.isItalic === false);
        if(!this.hasTheme() && this.options.model.color) {
            element.css("color", "#" + this.options.model.color);
        } else {
            element.css("color", "");
        }
    },

    /**
     * Called by some implementers of PrompInput to set standard styling on a prompt element
     * @param element
     */
    setElementStyle: function(element) {
        this._setFontStyle(element);
        this._setFontSizeAndFamily();
    },

    _setFontSizeAndFamily: function() {
        var container = this.element.parent();
        container.removeClass("times arial verdana");
        container.removeClass(function(i, css) {
            return (css.match(/(\bfont\d+\b)/g) || []).join(" ");
        });
        if(this.hasTheme()) {
            return;
        }
        if(this.options.model.fontFamily) {
            container.addClass(this.options.model.fontFamily);
        }
        if(this.options.model.fontSize) {
            container.addClass("font" + this.options.model.fontSize);
        }
    },

    /**
     * Sets the size of the prompt input
     */
    updateSize: function(skipSizeReset) {
        // reset to default
        this.element.parent().css(this.styleWidthProperty(), "");
        this.element.css(this.styleWidthProperty(), "");
        if(this.options.width !== undefined && this.options.width !== null && this.options.width >= 0 && this.options.height !== undefined && this.options.height !== null && this.options.height >= 0) {
            // restore model size in case prompt input size was auto adjusted based on style
            if(skipSizeReset !== true) {
                this.options.width = this.options.model.width;
                this.options.height = this.options.model.height;
            }
            this.setCorrectedSize();
            if(this.options.model.label !== null && (this.options.model.labelPosition === "TOP" || this.options.model.labelPosition === "BOTTOM")) {
                this.element.css(this.styleWidthProperty(), this.options.width + "px");
            } else {
                this.element.parent().css(this.styleWidthProperty(), this.options.width + "px");
            }
        }
    },

    /**
     * The property used for setting width on the prompt input style
     * @returns {string}
     */
    styleWidthProperty: function() {
        return "width";
    },

    setFlex: function(grow, shrink, basis) {
        this.wrapper.css("-ms-flex", grow + " " + shrink + " " + basis + "px");
        this.wrapper.css("-webkit-flex", grow + " " + shrink + " " + basis + "px");
        this.wrapper.css("flex", grow + " " + shrink + " " + basis + "px");
    },

    updateFlex: function() {
        var type = this.options.model.get("growType");
        if(type === GrowType.FREE) {
            this.wrapper.addClass("freeWidth");
            this.setFlex(this.options.model.renderedWidth, this.options.model.renderedWidth, this.options.model.renderedWidth);
        } else if(type === GrowType.FIXED) {
            this.wrapper.addClass("fixedWidth");
            this.wrapper.css("max-width", this.options.model.renderedWidth + "px");
            this.setFlex(this.options.model.renderedWidth, this.options.model.renderedWidth, this.options.model.renderedWidth);
        } else if(type === GrowType.SHRINK) {
            this.wrapper.addClass("shrinkWidth");
            this.setFlex(0, this.options.model.renderedWidth, this.options.model.renderedWidth);
        } else {
            this.wrapper.addClass("boundedWidth");
            this.setFlex(this.options.model.renderedWidth, this.options.model.renderedWidth, this.options.model.renderedWidth);
        }
    },

    /**
     * Enforces min size restrictions on the prompt input
     */
    setCorrectedSize: function() {
        let corrected = false;
        var minSize = this.getCorrectedMinSize();
        if(minSize.width > this.options.width) {
            this.options.width = minSize.width;
            corrected = true;
        }
        if(minSize.height > this.options.height) {
            this.options.height = minSize.height;
            corrected = true;
        }
        if(corrected) {
            this.trigger("sizeCorrected", {
                promptInput: this
            });
        }
    },

    /**
     * Gets the minimum size of the prompt input while taking contents into consideration
     * @returns {{width: number, height: number}}
     */
    getCorrectedMinSize: function() {
        var minSize = this.getMinSize();
        var model = this.options.model;
        // don't allow control to be resized less than label size
        if(model.hasLabel()) {
            var label = this.label.find(".labelText");
            var labelWidth = model.labelPosition !== "LEFT" ? label.width() : this.label.innerWidth();
            var labelHeight = label.height();
            // adds left position in case the required class is on the label (asterisk width)
            if((model.required || model.requiredMacro) && model.labelPosition !== "LEFT") {
                labelWidth += (label.position().left - this.label.position().left);
            }
            if(model.labelPosition === "TOP" || model.labelPosition === "BOTTOM") {
                // don't let width get shorter than label
                if(labelWidth > minSize.width) {
                    minSize.width = labelWidth;
                }
                // don't let height get shorter than min height + label height
                minSize.height += labelHeight;
            } else {
                // don't let height get shorter than label
                if(labelHeight > minSize.height) {
                    minSize.height = labelHeight;
                }
                // don't let width get shorter than min width + label width
                minSize.width += labelWidth;
            }
        } else if(this.measure !== undefined) {
            var texts = this.text().split(/\s+/g);
            var longest = 0;
            texts.forEach(function(text) {
                var width = this.measure.html(text).width();
                if(width > longest) {
                    longest = width;
                }
            }, this);
            if(longest > minSize.width) {
                minSize.width = longest;
            }
        }
        return minSize;
    },

    /**
     * Gets the minimum size of the prompt input's input
     * @returns {{width: number, height: number}}
     */
    getMinSize: function() {
        return {
            width: 0,
            height: 0
        };
    },

    /**
     * Gets the full width of the prompt container
     */
    getFullWidth: function() {
        return this.wrapper.width();
    },

    /**
     * Gets the full height of the prompt container
     */
    getFullHeight: function() {
        return this.wrapper.height();
    },

    /**
     * Gets the width of just the input
     */
    getInputWidth: function() {
        return this.input.outerWidth(true);
    },

    /**
     * Gets the height of just the input
     */
    getInputHeight: function() {
        return this.input.outerHeight(true);
    }
});
export default widget;
